import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Link } from 'react-router-dom'
import mbllogo from "../../assets/images/web_logo.png"
import logoSm from "../../assets/images/new_web_logo_only.png"
import { Input } from 'reactstrap'
import LanguageDropdown from '../CommonForBoth/TopbarDropdown/LanguageDropdown'
import ProfileMenu from '../CommonForBoth/TopbarDropdown/ProfileMenu'
import Sidebar from '../VerticalLayout/Sidebar'
import { createSelector } from 'reselect'
import { changeSidebarTheme } from '../../store/actions'
import { useDispatch, useSelector } from 'react-redux'
import { api } from "../../utils/variables"
import { POLL_INTERVAL_MS } from '../../config/syncConfig'
import axios from "axios"
import GlobalSearchBar from '../Common/GlobalSearchBar'
import { userProfileApi } from '../../services/userProfileApi'
import WebsiteNotificationBell from '../CommonForBoth/TopbarDropdown/WebsiteNotificationBell'
import '../../pages/UserProfile/PlansComponent.css'

// Login Redirect Utility
import { navigateToLogin, navigateToRegister } from '../../utils/loginRedirect'

const APP_VERSION = '3.0.0'
const DesktopHeader = () => {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)
  const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false)
  const [username, setUsername] = useState('')
  const [showProfileTutorial, setShowProfileTutorial] = useState(false)
  const [isGlobalTutorialOverlayActive, setIsGlobalTutorialOverlayActive] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const profileMenuRef = useRef(null)
  const [trialDaysLeft, setTrialDaysLeft] = useState(null)
  const [isTrialActive, setIsTrialActive] = useState(false)
  const isTablet = windowWidth >= 768 && windowWidth < 1024
  const toggleInfoPanel = () => setIsInfoPanelOpen(prev => !prev)
  const closeInfoPanel = (e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation()
    setIsInfoPanelOpen(false)
  }
  useEffect(() => {
    // Close the info panel when clicking anywhere outside it
    if (!isInfoPanelOpen) return
    const handleDocClick = () => {
      setIsInfoPanelOpen(false)
    }
    document.addEventListener('click', handleDocClick)
    return () => document.removeEventListener('click', handleDocClick)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInfoPanelOpen])
  const dispatch = useDispatch()
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  const navigate = useNavigate()
  const location = useLocation()
  const [activeButton, setActiveButton] = useState(null)

  // Keep activeButton only while location matches the button's target route.
  // Clear it when user navigates to a different page.
  useEffect(() => {
    if (!activeButton) return
    const map = {
      discount: '/top-discounts',
      trending: '/trending',
      newRelease: '/new-releases',
    }
    const target = map[activeButton]
    if (!target) return
    const pathname = (location && location.pathname) || ''
    if (pathname === target || pathname.startsWith(target + '/')) {
      // still on the button's page — keep active
      return
    }
    // navigated away — clear active
    setActiveButton(null)
  }, [location && location.pathname, activeButton])

  const activeButtonStyle = {
    background: '#ffffff',
    color: '#1F2230',
    border: '1px solid rgba(0,0,0,0.08)',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  }

  const navButtonStyle = {
    background: 'hsla(0, 32%, 89%, 0.04)',
    color: '#E7E9EF',
    border: '1px solid rgba(255,255,255,0.06)',
    padding: '4px 10px',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 12,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    transition: 'background 150ms, color 150ms, transform 120ms',
  }

  const selectLayoutState = state => state.Layout
  const selectLayoutProperties = createSelector(selectLayoutState, layout => ({
    leftSideBarTheme: layout.leftSideBarTheme,
    sidebarSizeType: layout.sidebarSizeType,
  }))

  const {
    leftSideBarTheme,
    sidebarSizeType,
  } = useSelector(selectLayoutProperties)

  useEffect(() => {
    if (leftSideBarTheme) {
      dispatch(changeSidebarTheme(leftSideBarTheme))
    }
  }, [leftSideBarTheme, dispatch])

  useEffect(() => {
    // Robustly try to read username from common storage keys and authUser object
    const tryKeys = ['userName', 'username', 'user_fullname', 'fullName', 'name', 'displayName', 'user']
    for (const k of tryKeys) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k)
      if (v) {
        setUsername(v)
        return
      }
    }

    const authUserRaw = localStorage.getItem('authUser') || sessionStorage.getItem('authUser')
    if (authUserRaw) {
      try {
        const obj = JSON.parse(authUserRaw)
        const candidate = obj.displayName || obj.username || obj.name || obj.fullName || obj.email || ''
        if (candidate) setUsername(candidate)
      } catch (e) {
        // ignore parse errors
      }
    }
  }, [])

  // Watch for global tutorial overlays (keyword/app tracking/profile/Ask AI) and
  // disable header interaction + blur while any overlay is active.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return

    const checkOverlay = () => {
      try {
        const hasOverlay = !!document.querySelector('.kt-backdrop-overlay, .profile-tutorial-backdrop-overlay')
        setIsGlobalTutorialOverlayActive(hasOverlay)
      } catch (e) {
        // ignore
      }
    }

    // Initial check
    checkOverlay()

    const observer = new MutationObserver(() => {
      checkOverlay()
    })

    try {
      observer.observe(document.body, { childList: true, subtree: true })
    } catch (e) {
      // ignore
    }

    window.addEventListener('keywordTutorialStep', checkOverlay)
    window.addEventListener('skipAllKeywordTutorial', checkOverlay)

    return () => {
      observer.disconnect()
      window.removeEventListener('keywordTutorialStep', checkOverlay)
      window.removeEventListener('skipAllKeywordTutorial', checkOverlay)
    }
  }, [])

  // Track window width for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Monitor sidebar collapsed state from Redux
  useEffect(() => {
    if (sidebarSizeType) {
      const isCollapsed = sidebarSizeType === 'condensed' || sidebarSizeType === 'sm'
      console.log("Sidebar size type:", sidebarSizeType, "Is collapsed:", isCollapsed)
      setIsSidebarCollapsed(isCollapsed)
    }
  }, [sidebarSizeType])

  // Fetch current plan and check for active trial
  useEffect(() => {
    const fetchTrialInfo = async () => {
      try {
        const result = await userProfileApi.fetchCurrentPlan()
        if (result.data) {
          const subscriptionStatus = result.data.status || result.data.subscription_status || null
          const subscriptionEnd = result.data.end_date || result.data.trial_end || result.data.expires_at || null

          if (subscriptionStatus === 'trialing' && subscriptionEnd) {
            const endMs = new Date(subscriptionEnd).getTime()
            const nowMs = Date.now()
            if (Number.isFinite(endMs) && endMs > nowMs) {
              const DAY_MS = 24 * 60 * 60 * 1000
              const daysLeft = Math.max(0, Math.ceil((endMs - nowMs) / DAY_MS))
              setTrialDaysLeft(daysLeft)
              setIsTrialActive(true)
              return
            }
          }
        }
        setIsTrialActive(false)
        setTrialDaysLeft(null)
      } catch (error) {
        console.error('Error fetching trial info:', error)
        setIsTrialActive(false)
        setTrialDaysLeft(null)
      }
    }

    fetchTrialInfo()
    // Refresh trial info every hour
    const interval = setInterval(fetchTrialInfo, 60 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  // Check if we're on keyword explorer page and show profile tutorial (step 3)
  // Also mark tutorial as completed when leaving the page
  useEffect(() => {
    const pathname = location?.pathname || ''
    const isKeywordExplorerPage = pathname === '/keyword-analysis' || pathname.startsWith('/keyword-analysis')

    // Mark tutorial as completed when leaving the keyword explorer page
    if (!isKeywordExplorerPage) {
      try {
        const tutorialSeen = sessionStorage.getItem('keywordExplorerOnboardingSeen') === 'true'
        const currentStep = sessionStorage.getItem('keywordTutorialCurrentStep')

        // If tutorial is active (not seen and has a current step), mark it as completed
        if (!tutorialSeen && currentStep && (currentStep === '0' || currentStep === '1' || currentStep === '2' || currentStep === '3' || currentStep === '4')) {
          sessionStorage.setItem('keywordExplorerOnboardingSeen', 'true')
          sessionStorage.removeItem('keywordTutorialCurrentStep')
          window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: -1 } }))

          // Try to mark as completed via API (non-blocking)
          fetch('/api/onboarding/complete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: 'keyword_explorer' })
          }).catch(() => null)
        }
      } catch (e) {
        // ignore storage errors
      }
      setShowProfileTutorial(false)
      return
    }

    if (!username) {
      setShowProfileTutorial(false)
      return
    }

    // Check if keyword explorer tutorial is active and we're on step 3
    const checkTutorialStep = () => {
      const tutorialSeen = sessionStorage.getItem('keywordExplorerOnboardingSeen') === 'true'
      const currentStep = sessionStorage.getItem('keywordTutorialCurrentStep')

      // Show profile tutorial if we're on step 3 and tutorial hasn't been completed
      if (!tutorialSeen && currentStep === '3') {
        setShowProfileTutorial(true)
      } else {
        setShowProfileTutorial(false)
      }
    }

    // Listen for tutorial step changes via custom event
    const handleTutorialStep = (e) => {
      if (e.detail?.step === 3) {
        setShowProfileTutorial(true)
      } else if (e.detail?.step !== 3) {
        setShowProfileTutorial(false)
      }
    }

    window.addEventListener('keywordTutorialStep', handleTutorialStep)

    // Check periodically for step changes
    const interval = setInterval(checkTutorialStep, 300)

    // Initial check
    checkTutorialStep()

    return () => {
      window.removeEventListener('keywordTutorialStep', handleTutorialStep)
      clearInterval(interval)
    }
  }, [location?.pathname, username])

  // Position profile tutorial highlight and arrow dynamically
  useEffect(() => {
    if (!showProfileTutorial) return

    const findProfileButton = () => {
      // First try using the ref
      if (profileMenuRef.current) {
        const button = profileMenuRef.current.querySelector('#page-header-user-dropdown') ||
          profileMenuRef.current.querySelector('button')
        if (button) return button
      }

      // Fallback: Try to find the profile button - it could be the DropdownToggle or the button inside it
      let profileButton = document.getElementById('page-header-user-dropdown')

      // If not found, try to find the button element inside the dropdown
      if (!profileButton) {
        const dropdown = document.querySelector('[id="page-header-user-dropdown"]')
        if (dropdown) {
          const innerButton = dropdown.querySelector('button')
          if (innerButton) {
            profileButton = innerButton
          }
        }
      }

      // If still not found, try finding by class or structure
      if (!profileButton) {
        const profileMenu = document.querySelector('.header-item')
        if (profileMenu) {
          const innerButton = profileMenu.querySelector('button')
          if (innerButton) {
            profileButton = innerButton
          }
        }
      }

      return profileButton
    }

    const updatePositions = () => {
      const profileButton = findProfileButton()

      if (!profileButton) {
        return
      }

      const rect = profileButton.getBoundingClientRect()

      // Make sure the element is visible
      if (rect.width === 0 || rect.height === 0) {
        return
      }

      // Highlight box around profile button
      const highlightBox = document.querySelector('.profile-tutorial-highlight-box')
      if (highlightBox) {
        highlightBox.style.top = `${rect.top - 4}px`
        highlightBox.style.left = `${rect.left - 4}px`
        highlightBox.style.width = `${rect.width + 8}px`
        highlightBox.style.height = `${rect.height + 8}px`
      }

      // Explanation box - positioned to the left of the profile button
      const explanationBox = document.querySelector('.profile-tutorial-explanation-box')
      if (explanationBox) {
        // First reset so we can measure actual size
        explanationBox.style.top = '0px'
        explanationBox.style.left = '0px'

        requestAnimationFrame(() => {
          const boxRect = explanationBox.getBoundingClientRect()
          const viewportWidth = window.innerWidth
          const viewportHeight = window.innerHeight
          const isMobileNow = viewportWidth <= 768
          const isTablet = viewportWidth > 768 && viewportWidth <= 1024

          if (isMobileNow) {
            // Mobile: Position box below the profile button, centered
            const gap = 16
            let boxTop = rect.bottom + gap
            const boxLeft = 16 // Use CSS left: 16px from media query

            // Ensure box stays within viewport
            if (boxTop + boxRect.height > viewportHeight - 16) {
              boxTop = Math.max(16, viewportHeight - boxRect.height - 16)
            }

            explanationBox.style.top = `${boxTop}px`
          } else {
            // Desktop/Tablet: Position to the left of button
            const gap = isTablet ? 40 : 56
            const verticalOffset = 20 // move box down a bit
            let boxTop = rect.top + rect.height / 2 - boxRect.height / 2 + verticalOffset
            let boxLeft = rect.left - boxRect.width - gap

            // Ensure box stays within viewport — the profile button sits
            // near the very top of the page, so vertically centering the
            // (fairly tall) box on it can push its top edge above y:0,
            // clipping the box's own heading text off-screen with no way
            // to scroll it into view (it's position:fixed). Same clamp
            // already used for the horizontal axis below, and for other
            // tutorial steps elsewhere.
            if (boxTop < 16) {
              boxTop = 16
            } else if (boxTop + boxRect.height > viewportHeight - 16) {
              boxTop = viewportHeight - boxRect.height - 16
            }
            if (boxLeft < 16) {
              boxLeft = 16
            }

            explanationBox.style.top = `${boxTop}px`
            explanationBox.style.left = `${boxLeft}px`

            // Arrow pointing from box to button (rightward)
            const arrow = document.querySelector('.profile-tutorial-arrow')
            if (arrow) {
              // Draw the line from the box's edge to the BUTTON's actual
              // center — not the box's own (possibly-clamped) center. When
              // the viewport clamp above moves the box away from where a
              // pure vertical-center would've put it, a horizontal-only
              // line drawn at the box's center no longer reaches the
              // button at all; a diagonal line (via rotation) always
              // points at the real target regardless of clamping.
              const boxCenterY = boxTop + boxRect.height / 2
              const boxRight = boxLeft + boxRect.width
              const highlightLeft = rect.left - 4 // highlight border extends 4px beyond button
              const buttonCenterY = rect.top + rect.height / 2
              const dx = highlightLeft - boxRight
              const dy = buttonCenterY - boxCenterY
              const length = Math.max(Math.sqrt(dx * dx + dy * dy), 0)
              const angle = (Math.atan2(dy, dx) * 180) / Math.PI

              arrow.style.top = `${boxCenterY}px`
              arrow.style.left = `${boxRight}px`
              arrow.style.width = `${length}px`
              arrow.style.height = '3px'
              arrow.style.transform = `rotate(${angle}deg)`
              arrow.style.transformOrigin = '0 50%'
            }
          }
        })
      }
    }

    // Initial positioning with multiple retries
    const tryUpdatePositions = (attempt = 0) => {
      const profileButton = findProfileButton()
      if (profileButton) {
        updatePositions()
      } else if (attempt < 5) {
        // Retry up to 5 times with increasing delays
        setTimeout(() => tryUpdatePositions(attempt + 1), 200 * (attempt + 1))
      }
    }

    // Start trying immediately and with retries
    tryUpdatePositions()
    updatePositions()

    window.addEventListener('resize', updatePositions)
    window.addEventListener('scroll', updatePositions, true)

    return () => {
      window.removeEventListener('resize', updatePositions)
      window.removeEventListener('scroll', updatePositions, true)
    }
  }, [showProfileTutorial])

  const handleCloseProfileTutorial = async () => {
    setShowProfileTutorial(false)
    try {
      // Mark keyword explorer tutorial as completed
      sessionStorage.setItem('keywordExplorerOnboardingSeen', 'true')
      sessionStorage.removeItem('keywordTutorialCurrentStep')
      window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: -1 } }))

      // Trigger completion in keyword explorer context
      const skipEvent = new CustomEvent('skipAllKeywordTutorial')
      window.dispatchEvent(skipEvent)

      // Mark onboarding as completed via API
      // Import and use markPageCompleted if available, or dispatch event
      try {
        const response = await fetch('/api/onboarding/complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ page: 'keyword_explorer' })
        }).catch(() => null)
        // If API not available, that's okay - sessionStorage is set
      } catch (e) {
        // API call failed, but that's okay
      }
    } catch (e) {
      // ignore storage errors
    }
  }




  return (
    <>
      <div className='display-desktop-nav'>
        <Sidebar
          theme={leftSideBarTheme}
          // type={leftSideBarType}
          isMobile={isMobile}
        />
        <div
          className='desk_header'
          style={isGlobalTutorialOverlayActive ? { pointerEvents: 'none', filter: 'blur(3px)' } : undefined}
        >
          <div className="container-fluid d-flex align-items-center justify-content-between"
            style={{
              height: "100%",
              flexWrap: "nowrap",
              paddingLeft: isTablet ? "12px" : "16px",
              paddingRight: isTablet ? "12px" : "16px"
            }}
          >


            {/* Left: Logo when sidebar collapsed OR Greeting when expanded */}
            <div
              className="d-flex align-items-center"
              style={{ flexShrink: 0, gap: '12px' }}
            >
              {/* Removed compact header logo when sidebar is collapsed */}

              {username && !isSidebarCollapsed && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ color: '#fff', fontWeight: 600, fontSize: 14, flexShrink: 0, marginLeft: -16 }}>
                    {(() => {
                      // Extract first name only (split by space, comma, or underscore)
                      const firstName = username.split(/[\s,_]+/)[0] || username;
                      return `Hi, ${firstName}`;
                    })()}
                  </div>

                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0, whiteSpace: 'nowrap' }}>
                    <div style={{ color: '#E7E9EF', fontWeight: 600, fontSize: 12, flexShrink: 0 }}>
                      Rankings:
                    </div>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        type="button"
                        aria-label="Discount"
                        onClick={() => { setActiveButton('discount'); navigate('/top-discounts') }}
                        style={{ ...navButtonStyle, ...(activeButton === 'discount' ? activeButtonStyle : {}) }}
                      >
                        Top Discount Apps
                      </button>
                      {/* Small red sale ribbon on the right-top of the button */}
                    </div>
                    <button
                      type="button"
                      aria-label="Trending"
                      onClick={() => { setActiveButton('trending'); navigate('/trending') }}
                      style={{ ...navButtonStyle, ...(activeButton === 'trending' ? activeButtonStyle : {}) }}
                    >
                      Trending Apps
                    </button>
                    <button
                      type="button"
                      aria-label="New Release"
                      onClick={() => { setActiveButton('newRelease'); navigate('/new-releases') }}
                      style={{ ...navButtonStyle, ...(activeButton === 'newRelease' ? activeButtonStyle : {}) }}
                    >
                      New Release Apps
                    </button>
                  </div>

                  {/* Trial Badge - shown when user has active trial, positioned after rankings nav */}
                  {isTrialActive && trialDaysLeft !== null && trialDaysLeft > 0 && (
                    <span
                      className="trial-badge"
                      onClick={() => navigate('/profile')}
                      style={{ cursor: 'pointer' }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate('/profile');
                        }
                      }}
                    >
                      Trial: {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left
                    </span>
                  )}
                  <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', flex: 1 }} />
                </div>
              )}
            </div>

            {/* Center area intentionally left minimal; quick-nav is shown beside username */}
            {/* Center: Search Bar */}
            <div className="d-flex flex-grow-1 justify-content-center" style={{
              minWidth: 0,
              marginLeft: "8px",
              marginRight: "12px"
            }}>
              <GlobalSearchBar
                style={{
                  height: isTablet ? "36px" : "38px",
                  maxWidth: isTablet ? "480px" : "580px",
                  borderRadius: isTablet ? "24px" : "26px",
                  fontSize: "13px"
                }}
                placeholder="Search here ..."
              />
            </div>

            {/* Right: Profile, Language & Mobile Search */}
            <div className="d-flex align-items-center" style={{ gap: '12px', flexShrink: 0 }}>

              {/* Language Dropdown */}
              <LanguageDropdown />

              {/* Notification Bell Icon */}
              {username && <WebsiteNotificationBell />}

              {/* Info icon */}
              <span id="infoIcon" style={{ position: "relative" }} role="button" aria-haspopup="dialog" aria-controls="infoPanel">
                <span
                  className="kq-icon-pill kq-icon-pill--square"
                  onClick={(e) => { if (e && typeof e.stopPropagation === 'function') e.stopPropagation(); toggleInfoPanel() }}
                  aria-label="KeyQuest info"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="9.25" stroke="#fff" strokeWidth="1.5" />
                    <rect x="11.1" y="10.4" width="1.8" height="6.4" rx="0.9" fill="#fff" />
                    <circle cx="12" cy="7.4" r="1.15" fill="#fff" />
                  </svg>
                </span>
                {isInfoPanelOpen && (
                  <div
                    id="infoPanel"
                    role="dialog"
                    aria-label="KeyQuest information"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: 32,
                      right: 0,
                      minWidth: 240,
                      background: '#1F2230',
                      color: '#E7E9EF',
                      border: '1px solid #2E3244',
                      borderRadius: 8,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                      overflow: 'hidden',
                      zIndex: 1050,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        background: '#1B1E2A',
                        borderBottom: '1px solid #2E3244',
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 14 }}>About</div>
                      <button
                        onClick={closeInfoPanel}
                        aria-label="Close info panel"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#9AA0AE',
                          fontSize: 18,
                          lineHeight: 1,
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div style={{ padding: 12, fontSize: 13, color: '#E7E9EF', lineHeight: 1.6 }}>
                      <div>Keyquest version 0.1.1</div>
                      <div>© 2026 by KeyQuest. All rights reserved.</div>
                    </div>
                  </div>
                )}
              </span>

              {/* Profile Dropdown or Auth Buttons */}
              {username ? (
                <div style={{ position: 'relative' }} ref={profileMenuRef}>
                  {showProfileTutorial && (
                    <>
                      <div className="profile-tutorial-backdrop-overlay"></div>
                      <div className="profile-tutorial-highlight-box"></div>
                    </>
                  )}
                  <div className={showProfileTutorial ? 'profile-tutorial-highlighted' : ''}>
                    <ProfileMenu />
                  </div>
                </div>
              ) : (
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigateToLogin(location.pathname, navigate)}
                    style={{ ...navButtonStyle, background: 'transparent', border: '1px solid rgba(255,255,255,0.2)' }}
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    onClick={() => navigateToRegister(location.pathname, navigate)}
                    style={{ ...navButtonStyle, background: '#F6255A', color: '#fff', border: 'none' }}
                  >
                    Sign Up
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Tutorial Explanation Box */}
      {showProfileTutorial && (
        <div className="profile-tutorial-explanation-box">
          <button
            type="button"
            className="profile-tutorial-skip-btn"
            onClick={async () => {
              await handleCloseProfileTutorial()
              // Also trigger skip all in the keyword explorer context if available
              try {
                const event = new CustomEvent('skipAllKeywordTutorial')
                window.dispatchEvent(event)
              } catch (e) { }
            }}
          >
            Skip All
          </button>
          <div className="profile-tutorial-text">
            If you want to upgrade your experience visit <strong>profile and billing</strong> - click here in <strong>settings</strong>
          </div>
          <div className="profile-tutorial-arrow"></div>
          <div className="profile-tutorial-nav">
            <button
              type="button"
              className="profile-tutorial-nav-btn profile-tutorial-nav-btn--prev"
              onClick={() => {
                setShowProfileTutorial(false)
                sessionStorage.setItem('keywordTutorialCurrentStep', '2')
                window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: 2 } }))
              }}
            >
              &lt; Prev
            </button>
            <button
              type="button"
              className="profile-tutorial-nav-btn profile-tutorial-nav-btn--next"
              onClick={() => {
                // Move from profile tutorial (step 3) to Ask AI tutorial (step 4)
                setShowProfileTutorial(false)
                try {
                  sessionStorage.setItem('keywordTutorialCurrentStep', '4')
                  window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: 4 } }))
                } catch (e) {
                  // ignore storage errors
                }
              }}
            >
              Next &gt;
            </button>
          </div>
        </div>
      )}

      <style>{`
        /* Profile Tutorial Styles */
        .profile-tutorial-backdrop-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 999;
          /* Block interaction with everything under the backdrop while profile tutorial is active */
          pointer-events: auto;
          backdrop-filter: blur(3px);
        }

        .profile-tutorial-highlight-box {
          position: fixed;
          border: 2px solid rgba(246, 37, 90, 0.8);
          border-radius: 15px;
          pointer-events: none;
          z-index: 1000;
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
          animation: profile-tutorial-pulse 2s ease-in-out infinite;
        }

        @keyframes profile-tutorial-pulse {
          0%, 100% { border-color: rgba(246, 37, 90, 0.6); }
          50% { border-color: rgba(246, 37, 90, 1); }
        }

        .profile-tutorial-highlighted {
          position: relative;
          z-index: 1001;
          animation: profile-tutorial-button-zoom 2s ease-in-out infinite;
        }

        @keyframes profile-tutorial-button-zoom {
          0%, 100% { 
            transform: scale(1);
          }
          50% { 
            transform: scale(1.05);
          }
        }

        .profile-tutorial-explanation-box {
          position: fixed;
          background: #fff;
          border: 1px solid #ddd;
          border-radius: 12px;
          padding: 20px 16px 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
          z-index: 1002;
          min-width: 280px;
          max-width: 320px;
          visibility: visible;
          opacity: 1;
          display: block;
          box-sizing: border-box;
        }

        .profile-tutorial-skip-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          background: transparent;
          border: none;
          color: #999;
          font-size: 12px;
          line-height: 1;
          cursor: pointer;
          padding: 6px 8px;
          min-height: 32px;
          min-width: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .profile-tutorial-skip-btn:hover,
        .profile-tutorial-skip-btn:focus {
          color: #F6255A;
          background: rgba(246, 37, 90, 0.1);
          outline: none;
        }

        .profile-tutorial-skip-btn:active {
          transform: scale(0.95);
        }

        .profile-tutorial-text {
          font-size: 14px;
          color: #333;
          line-height: 1.6;
          margin-top: 15px;
          margin-bottom: 8px;
          word-wrap: break-word;
          white-space: normal;
        }

        .profile-tutorial-text strong {
          color: #F6255A;
          font-weight: 600;
        }

        .profile-tutorial-arrow {
          position: fixed;
          height: 3px;
          background: #F6255A;
          z-index: 1003;
          pointer-events: none;
        }

        .profile-tutorial-arrow::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 100%;
          width: 0;
          height: 0;
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
          border-left: 10px solid #F6255A;
          transform: translateY(-50%);
        }

        .profile-tutorial-nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 8px;
          padding-top: 10px;
          border-top: 1px solid #eee;
          gap: 8px;
        }

        .profile-tutorial-nav-btn {
          background: transparent;
          border: none;
          color: #666;
          font-size: 14px;
          cursor: pointer;
          padding: 8px 12px;
          min-height: 44px;
          min-width: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          transition: all 0.2s;
          flex: 1;
          font-weight: 500;
        }

        .profile-tutorial-nav-btn:hover,
        .profile-tutorial-nav-btn:focus {
          color: #F6255A;
          background: rgba(246, 37, 90, 0.1);
          outline: none;
        }

        .profile-tutorial-nav-btn:active {
          transform: scale(0.97);
        }

        .profile-tutorial-nav-btn--next {
          font-weight: 600;
          color: #F6255A;
          background: rgba(246, 37, 90, 0.1);
        }

        .profile-tutorial-nav-btn--next:hover,
        .profile-tutorial-nav-btn--next:focus {
          background: rgba(246, 37, 90, 0.2);
        }

        /* Tablet styles (768px - 1024px) */
        @media (max-width: 1024px) and (min-width: 769px) {
          .profile-tutorial-explanation-box {
            max-width: 300px;
            padding: 18px 14px 10px;
          }
          .profile-tutorial-text {
            font-size: 13px;
          }
        }

        /* Mobile styles */
        @media (max-width: 768px) {
          .profile-tutorial-explanation-box {
            min-width: calc(100vw - 32px);
            max-width: calc(100vw - 32px);
            width: calc(100vw - 32px);
            left: 16px !important;
            right: 16px !important;
            margin: 0 auto;
            border-radius: 16px;
            padding: 20px 16px 14px;
          }

          .profile-tutorial-text {
            font-size: 15px;
            line-height: 1.6;
            text-align: left;
            margin-top: 12px;
            margin-bottom: 10px;
          }

          .profile-tutorial-skip-btn {
            top: 10px;
            right: 10px;
            font-size: 13px;
            padding: 8px;
            min-height: 36px;
            min-width: 36px;
          }

          .profile-tutorial-nav {
            margin-top: 12px;
            padding-top: 12px;
            gap: 10px;
          }

          .profile-tutorial-nav-btn {
            font-size: 15px;
            padding: 10px 16px;
            min-height: 48px;
            border-radius: 8px;
          }

          /* Hide arrows on mobile for cleaner look */
          .profile-tutorial-arrow {
            display: none;
          }
        }

        /* Small mobile styles */
        @media (max-width: 480px) {
          .profile-tutorial-explanation-box {
            min-width: calc(100vw - 24px);
            max-width: calc(100vw - 24px);
            width: calc(100vw - 24px);
            left: 12px !important;
            right: 12px !important;
            padding: 18px 14px 12px;
          }

          .profile-tutorial-text {
            font-size: 14px;
          }

          .profile-tutorial-nav-btn {
            font-size: 14px;
            padding: 10px 14px;
          }
        }
      `}</style>

    </>
  )
}

export default DesktopHeader
